<script setup>
import GroupedActionPanel from "@/components/actions/partials/GroupedActionPanel.vue";
import HidePageActions from "@/components/actions/buttons/HidePageActions.vue";
import CompletePageActions from "@/components/actions/buttons/CompletePageActions.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ActionTime from "@/components/actions/partials/ActionTime.vue";
import ActionRow from "@/components/actions/partials/ActionRow.vue";
import {computed, ref} from "vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {useStore} from "vuex";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import InspectPage from "@/components/actions/partials/InspectPage.vue";
import ContentWizardButton from "@/components/actions/partials/settings/ContentWizardButton.vue";
import {useInfiniteQuery} from "@tanstack/vue-query";
import axios from "axios";
import {useInfiniteScroll} from "@vueuse/core";
import GroupedActionPanelTitle from "@/components/actions/partials/GroupedActionPanelTitle.vue";

const showAllPage = ref();
const maxShowActions = 5;
const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);

const toggleShowAll = (pageId) => {
  showAllPage.value = showAllPage.value === pageId ? undefined : pageId;
}

const {
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isPending,
} = useInfiniteQuery({
  queryKey: ['actions', activeTab],
  queryFn: async ({pageParam}) => {
    const {data} = await axios.get(`/ajax/v2/actions/by-page?page=${pageParam}`);

    return data;
  },
  initialPageParam: 1,
  getNextPageParam: (lastPage) => lastPage.meta.current_page + 1 <= lastPage.meta.last_page ? lastPage.meta.current_page + 1 : null,
});

const groupedActions = computed(() => {
  if (!data.value?.pages) return [];

  return Object.values(data.value.pages).flatMap(pagesGroup =>
    pagesGroup.data.map(page => {
      const totalTime = page.actions.reduce((a, b) => a + b.action_time, 0);
      const showAll = showAllPage.value === page.id;

      return {
        page,
        totalTime,
        actions: showAll ? page.actions : page.actions.slice(0, maxShowActions),
        totalActions: page.total_actions,
        moreActionsCount: Math.max(page.actions.length - maxShowActions, 0),
        showAll,
      };
    })
  );
});

const infiniteScroll = ref(null);
useInfiniteScroll(
  infiniteScroll,
  async () => {
    if (hasNextPage.value && !isFetchingNextPage.value) {
      await fetchNextPage();
    }
  },
  {distance: 1000}
);
</script>

<template>
  <div
    ref="infiniteScroll"
    class="tw-flex tw-flex-col tw-gap-10 tw-h-[72vh] tw-overflow-y-auto visible-scrollbar"
  >
    <LoadingBlock v-if="isPending" />

    <ZeroContent v-else-if="!groupedActions.length">
      Looks like there are no actions to display.
    </ZeroContent>

    <GroupedActionPanel
      v-for="group in groupedActions"
      v-else
      :key="group.page.id"
    >
      <template #title>
        <GroupedActionPanelTitle
          :title="group.page.url"
          url
        />
      </template>

      <template #action-time>
        <ActionTime
          :minutes="group.totalTime"
          class="tw-font-semibold"
        />
      </template>
      <template #toolbar>
        <div class="tw-flex tw-gap-2">
          <InspectPage
            v-if="group.page.status < 600 && !group.page.added_via_sitemap"
            :page="group.page"
          />
          <CompletePageActions :page-id="group.page.id" />
          <HidePageActions :page-id="group.page.id" />
          <ContentWizardButton
            :page="group.page"
            :has-keywords-assigned="group.page.has_keyword_assigned"
            buttonClasses="tw-h-[42px]"
          />
        </div>
      </template>

      <template #actions>
        <ActionRow
          v-for="action in group.actions"
          :key="action.id"
          :action="action"
        />
      </template>

      <template #pagination>
        <div
          v-if="group.moreActionsCount > 0"
          class="tw-flex tw-justify-end tw-align-middle tw-gap-2 tw-p-4"
        >
          <div
            class="tw-flex-grow-0 tw-flex tw-align-middle tw-p-4 tw-gap-2"
          >
            <svg-vue
              class="tw-h-4 tw-w-4"
              icon="icons/bar-chart-2"
            />
            <span v-if="!group.showAll">
              {{
                `${group.moreActionsCount} more ${group.moreActionsCount === 1 ? 'action' : 'actions'} for this page`
              }}
            </span>
            <span v-else>
              {{
                `Showing ${group.totalActions} ${group.totalActions === 1 ? 'action' : 'actions'} for this page`
              }}
            </span>
          </div>
          <DefaultButton
            variant="primary-inverted"
            @click="toggleShowAll(group.page.id)"
          >
            {{ group.showAll ? 'View Less' : 'View All' }}
          </DefaultButton>
        </div>
      </template>
    </GroupedActionPanel>

    <LoadingBlock v-if="isFetchingNextPage">
      Loading more...
    </LoadingBlock>
  </div>
</template>
